import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {RefreshControl} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useNavigation} from '@react-navigation/native';
import {useTheme} from 'styled-components/native';
import {format, parseISO} from 'date-fns';

import {Header} from '../../../../components/Interface/Headers/Header';
import {Titles} from '../../../../components/Interface/Typographies/Titles';
import {Button} from '../../../../components/Interface/Forms/Buttons/Button';
import {SpinnerLoading} from '../../../../components/shared/Payments/SpinnerLoading';
import {api} from '../../../../services/api';
import {
  PPTrackRequestResponseDTO,
  PPTrackRequestResultDTO,
} from '../../../../dtos/ProntoPagueiDTO';
import {NotFound} from '../../../../components/Interface/Search/NotFound';
import {IdpayVerificationGate} from '../../../../components/shared/IdpayVerificationBottomSheet';
import {useIdpayVerification} from '../../../../context/IdpayVerification';
import {useGlobal} from '../../../../context/global';
import {Maintenance} from '../../../../components/Interface/Maintenance';
import {RFValue} from 'react-native-responsive-fontsize';

import {
  Container,
  Content,
  ScrollWrapper,
  CardsList,
  InfoBanner,
  InfoBannerText,
  Footer,
  SummaryListCard,
  SummaryListDateWrapper,
  SummaryListDateLabel,
  SummaryListDateBold,
  SummaryListRight,
  StatusBadge,
  StatusBadgeText,
} from './styles';

function formatPurchaseDate(date?: string): string {
  if (!date) {return '—';}
  try {
    return format(parseISO(date), 'dd/MM/yyyy');
  } catch {
    return '—';
  }
}

function getStatusBadgeConfig(status: string, colors: Record<string, any>) {
  const lower = status.toLowerCase().replace(/_/g, ' ');
  if (lower.includes('liquid') || lower.includes('pago')) {
    return {label: 'Pago', bg: colors.success, textColor: '#FFFFFF'};
  }
  if (
    lower.includes('recus') ||
    lower.includes('cancel') ||
    lower.includes('reprov')
  ) {
    return {label: 'Recusado', bg: colors.red, textColor: '#FFFFFF'};
  }
  if (lower.includes('analise') || lower.includes('análise') || lower.includes('pendente')) {
    return {label: 'Em análise', bg: '#ffd064', textColor: '#FFFFFF'};
  }
  return {label: status, bg: colors.orange_opacity, textColor: colors.orange_dark};
}

export function PP_TrackRequests() {
  const theme = useTheme();
  const navigation = useNavigation();

  const [requests, setRequests] = useState<PPTrackRequestResultDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const {refresh: refreshIdpay} = useIdpayVerification();
  const {disabledFeatures} = useGlobal();
  const isDisabled = (key: string) => disabledFeatures.includes(key);

  const loadRequests = useCallback(async () => {
    refreshIdpay();

    setError('');
    setIsLoading(true);
    try {
      const {data} = await api.get<PPTrackRequestResponseDTO>(
        '/payments/consultar_liquidacao_pronto_paguei',
      );
      setRequests(data.resultados ?? []);
    } catch ({response}: any) {
      setError(
        response?.data?.message ??
          'Não foi possível carregar as solicitações de parcelamento. Tente novamente mais tarde.',
      );
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, [refreshIdpay]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const listContent = useMemo(() => {
    if (requests.length <= 0 || error.length > 0) {
      return <NotFound title="Nada encontrado." text={error} />;
    }

    return requests.map((item) => {
      const {label, bg, textColor} = getStatusBadgeConfig(item.status ?? '', theme.colors);

      return (
        <SummaryListCard
          key={item.protocolo}
          accentColor={bg}
          onPress={() =>
            (navigation as any).navigate('PP_TrackRequestDetail', {item})
          }>
          <SummaryListDateWrapper>
            <SummaryListDateLabel>Solicitado em</SummaryListDateLabel>
            <SummaryListDateBold>
              {formatPurchaseDate(item.data_compra)}
            </SummaryListDateBold>
          </SummaryListDateWrapper>
          <SummaryListRight>
            <StatusBadge style={{backgroundColor: bg}}>
              <StatusBadgeText style={{color: textColor}}>
                {label}
              </StatusBadgeText>
            </StatusBadge>
            <Feather
              name="chevron-right"
              size={RFValue(18)}
              color={theme.colors.text}
            />
          </SummaryListRight>
        </SummaryListCard>
      );
    });
  }, [error, navigation, requests, theme]);

  if (isDisabled('multas')) {
    return <Maintenance />;
  }

  return (
    <>
      {isLoading && <SpinnerLoading />}
      <Container>
        <Header icon="chevron-left" />
        <ScrollWrapper
          contentContainerStyle={{paddingBottom: RFValue(20), flexGrow: 1}}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                setRefreshing(true);
                loadRequests();
              }}
            />
          }>
          <Content>
            <Titles
              title="Acompanhar solicitações"
              subtitle="Veja aqui o status das suas solicitações de parcelamento de débitos."
            />
            <InfoBanner>
              <Feather name="clock" size={16} color={theme.colors.orange} />
              <InfoBannerText>
                Após a liquidação, a quitação dos débitos junto ao órgão
                competente pode levar até{' '}
                <InfoBannerText style={{fontWeight: 'bold'}}>
                  2 dias úteis
                </InfoBannerText>{' '}
                para ser processada.
              </InfoBannerText>
            </InfoBanner>
            <CardsList>{listContent}</CardsList>
          </Content>
        </ScrollWrapper>
      </Container>
      <Footer>
        <SafeAreaView edges={['bottom']}>
          <Button title="Atualizar" enabled={!isLoading} onPress={loadRequests} />
        </SafeAreaView>
      </Footer>

      <IdpayVerificationGate scope="screen" />
    </>
  );
}
